import { createContext, useContext, useState, useEffect, ReactNode, Dispatch, SetStateAction } from "react"
import { useNavigate } from 'react-router-dom'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import Axios from "axios"
import Swal from 'sweetalert2'
import i18n from 'i18next'
import { useDispatch } from "react-redux"
import { AppDispatch } from '../../store'

const t = i18n.t.bind(i18n)

// Tipo para el payload de JWT
interface CustomJwtPayload extends JwtPayload {
  rol?: string
  isAdmin?: boolean
  email?: string
  id?: string,
  name?: string,
  idSucursal?: string
}

// Tipo del contexto
interface AuthContextType {
  clientauth: CustomJwtPayload | null
  setClientauth: Dispatch<SetStateAction<CustomJwtPayload | null>>
  loginClient: (userCredentials: any) => Promise<void>
  logoutClient: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | null>(null)

const useLogoutClient = () => {
  const dispatch: AppDispatch = useDispatch()

  return async () => {
    dispatch({ type: 'USER_LOGOUT' })
    localStorage.removeItem("jwt")
  }
}

const useLoginClient = (setClientauth: Dispatch<SetStateAction<CustomJwtPayload | null>>) => {
  const navigate = useNavigate()

  return async (userCredentials: any) => {
    const options = {
      headers: { "Access-Control-Allow-Origin": "*" },
    }

    try {
      const result = await Axios.post(
        process.env.REACT_APP_DB_ADDRESS + "/api/login/in",
        { user: userCredentials.user, password: userCredentials.password, app: 'aico' },
        options
      )

      const token = result.data["token"]

      if (token && token !== "undefined") {
        localStorage.setItem("jwt", token)

        // Decodificamos el token
        const decoded = jwtDecode<CustomJwtPayload>(token)
        let rol = decoded.rol

        // Actualiza el estado de clientauth
        setClientauth(decoded)

        // Redirigir según el rol
        if (rol === 'admin') {
          return navigate(`/${t("url.admin")}/${t("url.dashboard")}`)
        } else if (rol === 'clerk') {
          return navigate(`/${t("url.clerk")}/${t("url.dashboard")}`)
        } else if (rol === 'commercial') {
          return navigate(`/${t("url.commercial")}/${t("url.dashboard")}`)
        } else {
          if (window.location.pathname !== '/') {
            return navigate('/')
          }
        }
      } else {
        // Token inválido
        Swal.fire({
          showConfirmButton: true,
          confirmButtonColor: '#3085d6',
          buttonsStyling: false,
          confirmButtonText: 'Cerrar',
          icon: 'error',
          text: result.data.message,
        })
      }
    } catch (error) {
      console.error("Error al iniciar sesión:", error)
      Swal.fire({
        showConfirmButton: true,
        confirmButtonColor: '#3085d6',
        buttonsStyling: false,
        confirmButtonText: 'Cerrar',
        icon: 'error',
        text: 'Ocurrió un error al intentar iniciar sesión',
      })
    }
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [clientauth, setClientauth] = useState<CustomJwtPayload | null>(null)
  const loginClient = useLoginClient(setClientauth)
  const logoutClient = useLogoutClient()
  const storedToken = localStorage.getItem("jwt")

  useEffect(() => {
    try {
      setClientauth(storedToken ? jwtDecode<CustomJwtPayload>(storedToken) : null)
    } catch (error) {
      setClientauth(null)
      localStorage.removeItem("jwt")
    }
  }, [storedToken])

  const contextValue: AuthContextType = { clientauth, loginClient, logoutClient, setClientauth }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth debe usarse dentro de un AuthProvider")
  return context
}
